import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { ClickMarketTrendsLink_ForYouHeuristic } from '@sorare/core/src/protos/events/platform/web/events';

import { CardRecommendationRow } from '../CardRecommendationRow';
import { NoWrap } from '../ui';
import {
  FriendsRecentPurchasesQuery,
  FriendsRecentPurchasesQueryVariables,
} from './__generated__/index.graphql';

const FRIENDS_RECENT_PURCHASES_QUERY = gql`
  query FriendsRecentPurchasesQuery(
    $so5LeagueSlug: String!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
  ) {
    forYou {
      friendsRecentPurchases(so5LeagueSlug: $so5LeagueSlug) {
        id
        card {
          slug
          ...CardRecommendationRow_anyCard
        }
      }
    }
  }
  ${CardRecommendationRow.fragments.anyCard}
` as TypedDocumentNode<
  FriendsRecentPurchasesQuery,
  FriendsRecentPurchasesQueryVariables
>;

type Props = {
  skip: boolean;
  onStatsClick: (slug: string) => void;
  so5LeagueSlug: string;
};

export const FriendsPurchases = ({
  skip,
  onStatsClick,
  so5LeagueSlug,
}: Props) => {
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();

  const { data, loading } = useQuery(FRIENDS_RECENT_PURCHASES_QUERY, {
    variables: {
      so5LeagueSlug,
      isIosApp,
      isAndroidApp,
      googlePlayStoreCountryCode,
    },
    skip,
  });

  const tokenOwners = data?.forYou?.friendsRecentPurchases;
  const cards = tokenOwners?.map(({ card }) => card).slice(0, 5) || [];

  return (
    <CardRecommendationRow
      title={
        <FormattedMessage
          defaultMessage="Friend's{br}Recent{br}<noWrap>Purchases</noWrap>"
          id="market.trending.forYou.friendsPurchases.title"
          values={{
            br: <br />,
            noWrap: chunks => <NoWrap>{chunks}</NoWrap>,
          }}
        />
      }
      background="linear-gradient(92deg, #BE604C 2.27%, #5B0092 96.89%)"
      loading={loading}
      cards={cards}
      onStatsClick={onStatsClick}
      eventProperties={{
        so5LeagueSlug,
        heuristic:
          ClickMarketTrendsLink_ForYouHeuristic.FRIENDS_RECENT_PURCHASES,
      }}
    />
  );
};
